import React, { useState, useEffect, useRef } from 'react';
import { translateConversation } from '../../../../api/conversations';

import styles from './index.module.css';

const TRANSLATION_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'ru', name: 'Russian' },
  { code: 'it', name: 'Italian' },
];

const SUPPORTED_LANGUAGE_CODES = TRANSLATION_LANGUAGES.map(({ code }) => code);

const LANGUAGE_NAMES = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  ru: 'Russian',
  it: 'Italian',
};

export const isSupportedTranslationLanguage = (code) => SUPPORTED_LANGUAGE_CODES.includes(code);
export const getLanguageName = (code) => LANGUAGE_NAMES[code] || code;

const TranslateIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox='0 0 256 256' fill='currentColor' xmlns='http://www.w3.org/2000/svg'>
    <path d='M247.15,212.42l-56-112a8,8,0,0,0-14.31,0l-21.71,43.43A88,88,0,0,1,108,126.93,103.65,103.65,0,0,0,135.69,64H160a8,8,0,0,0,0-16H104V32a8,8,0,0,0-16,0V48H32a8,8,0,0,0,0,16h87.63A87.76,87.76,0,0,1,96,116.35a87.74,87.74,0,0,1-19-31,8,8,0,1,0-15.08,5.34A103.63,103.63,0,0,0,84,127a87.55,87.55,0,0,1-52,17,8,8,0,0,0,0,16,103.46,103.46,0,0,0,64-22.08,104.18,104.18,0,0,0,51.44,21.31l-26.6,53.19a8,8,0,0,0,14.31,7.16L148.94,192h70.11l13.79,27.58A8,8,0,0,0,240,224a8,8,0,0,0,7.15-11.58ZM156.94,176,184,121.89,211.05,176Z' />
  </svg>
);

const TranslationToggle = ({ conversationUuid, initialLanguage, onLanguageChange }) => {
  const supportedInitialLanguage = isSupportedTranslationLanguage(initialLanguage) ? initialLanguage : '';
  const [enabled, setEnabled] = useState(!!supportedInitialLanguage);
  const [targetLanguage, setTargetLanguage] = useState(supportedInitialLanguage);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const supportedLanguage = isSupportedTranslationLanguage(initialLanguage) ? initialLanguage : '';
    setEnabled(!!supportedLanguage);
    setTargetLanguage(supportedLanguage);
    setMenuOpen(false);
  }, [conversationUuid, initialLanguage]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const closeMenu = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', closeMenu);
    return () => document.removeEventListener('mousedown', closeMenu);
  }, [menuOpen]);

  const disableTranslation = () => {
    const previousLanguage = targetLanguage;
    setEnabled(false);
    setMenuOpen(false);
    translateConversation(
      conversationUuid,
      { conversation: { target_language: null } },
      () => onLanguageChange(null),
      () => {
        setEnabled(true);
        setTargetLanguage(previousLanguage);
      }
    );
  };

  const selectLanguage = (newLang) => {
    if (!newLang) return;
    const previousLang = targetLanguage;
    const previousEnabled = enabled;

    setTargetLanguage(newLang);
    setEnabled(true);
    setMenuOpen(false);
    translateConversation(
      conversationUuid,
      { conversation: { target_language: newLang } },
      () => onLanguageChange(newLang),
      () => {
        setTargetLanguage(previousLang);
        setEnabled(previousEnabled);
        onLanguageChange(previousLang || null);
      }
    );
  };

  if (enabled) {
    return (
      <div
        className={`${styles.toggle_wrap} ${styles.toggle_wrap_on}`}
        ref={menuRef}
        role='group'
        aria-label='Translate Chat'
      >
        <span className={styles.translate_icon} aria-hidden='true'>
          <TranslateIcon />
        </span>
        <span className={styles.label} translate='no'>
          {getLanguageName(targetLanguage)}
        </span>
        <button
          type='button'
          className={styles.translate_close}
          onClick={disableTranslation}
          aria-label='Turn off translation'
          title='Turn off translation'
        >
          <svg width='11' height='11' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path d='M2 2L10 10M10 2L2 10' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className={styles.translation_menu_wrap} ref={menuRef}>
      <button
        type='button'
        className={styles.toggle_wrap}
        onClick={() => setMenuOpen((open) => !open)}
        aria-haspopup='listbox'
        aria-expanded={menuOpen}
        aria-label='Translate Chat'
        title='Translate Chat'
      >
        <span className={styles.translate_icon} aria-hidden='true'>
          <TranslateIcon />
        </span>
        <span className={styles.label}>Translate Chat</span>
      </button>
      {menuOpen && (
        <div className={styles.language_menu} role='listbox' aria-label='Select language'>
          {TRANSLATION_LANGUAGES.map(({ code, name }) => (
            <button
              key={code}
              type='button'
              role='option'
              aria-selected={targetLanguage === code}
              className={styles.language_option}
              onClick={() => selectLanguage(code)}
            >
              {name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TranslationToggle;
